import { useQuery } from '@apollo/client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import GET_CURRENT_TENANT from 'api/queries/GET_CURRENT_TENANT';
import useDebouncedCallbackPerKey from 'hooks/useDebounceCallbackPerKey';
import WaringSvg from 'images/v5/icons/badge-warning.svg';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import FilePlusSvg from 'images/v5/icons/file-plus-01.svg';
import Accordion from 'layout/atoms/Accordion/Accordion';
import Button from 'layout/atoms/Button/Button';
import ComponentBadge from 'layout/atoms/ComponentBadge/ComponentBadge';
import Header from 'layout/atoms/Header/Header';
import InputNumber from 'layout/atoms/InputNumber';
import Loader from 'layout/atoms/Loader/Loader';
import TableGrid, { Body, Cell, Head, Row } from 'layout/atoms/TableGrid';
import CurrencySelect from 'layout/molecules/CurrencySelect';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'layout/molecules/Grid/Grid';
import parseComponentsByCategory from 'parsers/specification/parseComponentsByCategory';
import { openModal } from 'store/layoutSlice';
import { fetchSpecificationComponentsCosts, selectComponentsCostsItems, selectComponentsCostsStatus, updateComponentVariantPrice, updateStoreComponentVariantPrice, } from 'store/specificationComponentsCostsSlice';
import { fetchSpecificationComponentHeaders, selectSpecificationComponentHeaders, selectSpecificationComponentHeadersRefetch, selectSpecificationComponentHeadersSpecificationId, selectSpecificationComponentHeadersStatus, shouldFetchSpecificationComponentQuery, } from 'store/specificationComponentsSlice';
import { fetchSpecificationProblems, selectStepValidationRequestId, } from 'store/specificationDetailsSlice';
import getCategoryLabel from 'utils/getCategoryLabel';
function ComponentCosts() {
    var _a, _b, _c;
    const { t, i18n } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const { data: tenantData } = useQuery(GET_CURRENT_TENANT, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
    });
    const specificationDetails = useSelector((state) => state.specificationDetails.item);
    const isBrandNew = useSelector((state) => state.specificationDetails.brandNewSpec);
    const isProduction = specificationDetails.state === 'PRODUCTION';
    const componentsStatus = useSelector(selectSpecificationComponentHeadersStatus);
    const componentsRefetch = useSelector(selectSpecificationComponentHeadersRefetch);
    const cachedSpecificationId = useSelector(selectSpecificationComponentHeadersSpecificationId);
    const components = useSelector(selectSpecificationComponentHeaders);
    const categoriezdComponents = parseComponentsByCategory(components);
    const componentsCostsItems = useSelector(selectComponentsCostsItems);
    const componentsCostsStatus = useSelector(selectComponentsCostsStatus);
    const defaultCurrencyIsoCode = ((_c = (_b = (_a = tenantData === null || tenantData === void 0 ? void 0 : tenantData.me) === null || _a === void 0 ? void 0 : _a.currentTenant) === null || _b === void 0 ? void 0 : _b.defaultCurrency) === null || _c === void 0 ? void 0 : _c.isoCode) || '';
    const hasProblems = (ids) => {
        var _a;
        return !isBrandNew &&
            ((_a = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.estimatedCostCalculationProblems) === null || _a === void 0 ? void 0 : _a.some((problem) => {
                var _a;
                return problem.problemType === 'missing_variant_price' &&
                    ((_a = problem.components) === null || _a === void 0 ? void 0 : _a.some((component) => {
                        const idArray = Array.isArray(ids) ? ids : [ids];
                        return idArray.includes(component.nodeId);
                    }));
            }));
    };
    const getComponentCosts = (componentId) => {
        var _a, _b;
        return (_b = (_a = componentsCostsItems
            .find((item) => item.id === componentId)) === null || _a === void 0 ? void 0 : _a.assignedProductVariantSnapshots) === null || _b === void 0 ? void 0 : _b.edges.map((cost) => cost.node);
    };
    const componentHasProblems = (componentId) => hasProblems((getComponentCosts(componentId) || []).map((variant) => variant.id));
    const openedOnStartIds = useRef(null);
    const [showErrors, setShowErrors] = useState(false);
    const stepValidationRequestId = useSelector(selectStepValidationRequestId);
    useEffect(() => {
        if (stepValidationRequestId > 0) {
            setShowErrors(true);
        }
    }, [stepValidationRequestId]);
    useEffect(() => {
        if (shouldFetchSpecificationComponentQuery(componentsStatus, componentsRefetch, cachedSpecificationId, specificationDetails.id)) {
            dispatch(fetchSpecificationComponentHeaders({ specificationId: specificationDetails.id }));
        }
        if (componentsCostsStatus.fetch === 'idle') {
            dispatch(fetchSpecificationComponentsCosts({ specificationId: specificationDetails.id }));
        }
    }, [
        dispatch,
        specificationDetails.id,
        componentsStatus,
        componentsRefetch,
        cachedSpecificationId,
        componentsCostsStatus,
    ]);
    const [debouncedUpdatePrice, savingKeys] = useDebouncedCallbackPerKey((productVariantSnapshotId, value) => {
        dispatch(updateComponentVariantPrice({
            price: parseFloat(value),
            productVariantSnapshotId,
        }))
            .unwrap()
            .then(() => {
            dispatch(fetchSpecificationProblems({ specificationId: specificationDetails.id }));
        });
    }, 1000);
    const handleCurrencyChange = useCallback((id, isoCode, name, price) => {
        dispatch(updateStoreComponentVariantPrice({
            price,
            currency: isoCode,
            currencyId: id,
            productVariantSnapshotId: name,
        }));
        dispatch(updateComponentVariantPrice({
            price,
            currencyId: id,
            productVariantSnapshotId: name,
        }))
            .unwrap()
            .then(() => {
            dispatch(fetchSpecificationProblems({ specificationId: specificationDetails.id }));
        });
    }, [dispatch, specificationDetails.id]);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        dispatch(updateStoreComponentVariantPrice({ price: value, productVariantSnapshotId: name }));
        debouncedUpdatePrice(name, value);
    };
    if (cachedSpecificationId !== specificationDetails.id ||
        componentsStatus === 'loading' ||
        componentsStatus === 'idle' ||
        componentsCostsStatus.fetch === 'loading' ||
        componentsCostsStatus.fetch === 'idle') {
        return React.createElement(Loader, null);
    }
    if (componentsStatus === 'succeeded' && components.length === 0) {
        return (React.createElement(EmptyState, { header: t('add_first_component'), subheader: t('add_first_component_subheader') },
            React.createElement(Button, { color: "primary", leftIcon: React.createElement(FilePlusSvg, null), onClick: () => dispatch(openModal({ name: 'componentsSelector' })) }, t('add_components'))));
    }
    const renderComponentRow = (component, aside) => (React.createElement(Row, null,
        React.createElement(Cell, null,
            React.createElement(ComponentBadge, { name: component.name, category: getCategoryLabel(component.productCategory, i18n), missingData: componentHasProblems(component.id) })),
        React.createElement(Cell, { align: "right" }, aside)));
    const renderVariantRow = (component, variant) => {
        var _a, _b, _c, _d, _e, _f, _g;
        return (React.createElement(Row, { key: variant.id },
            React.createElement(Cell, null,
                t('color_capitalize'),
                ": ",
                variant.color,
                component.productCategory.variantHasSize && `; ${t('size_capitalize')}: ${variant === null || variant === void 0 ? void 0 : variant.size}`),
            React.createElement(Cell, { align: "right" },
                React.createElement("div", { style: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '4px',
                    } },
                    (((_a = variant === null || variant === void 0 ? void 0 : variant.productVariant.currentPrice) === null || _a === void 0 ? void 0 : _a.price) !== (variant === null || variant === void 0 ? void 0 : variant.price) ||
                        ((_b = variant === null || variant === void 0 ? void 0 : variant.productVariant.currentPrice.currency) === null || _b === void 0 ? void 0 : _b.id) !== ((_c = variant === null || variant === void 0 ? void 0 : variant.currency) === null || _c === void 0 ? void 0 : _c.id)) &&
                        !(variant.price === null || variant.currency === null) && (React.createElement(Button, { size: "xs", shape: "rounded", color: "notice", leftIcon: React.createElement(WaringSvg, null), onClick: () => {
                            var _a, _b, _c;
                            return dispatch(openModal({
                                name: 'componentCostsPriceDiff',
                                data: {
                                    variantId: variant === null || variant === void 0 ? void 0 : variant.id,
                                    productVariantId: (_a = variant === null || variant === void 0 ? void 0 : variant.productVariant) === null || _a === void 0 ? void 0 : _a.id,
                                    price: (variant === null || variant === void 0 ? void 0 : variant.price) || 0,
                                    currencyId: (_b = variant === null || variant === void 0 ? void 0 : variant.currency) === null || _b === void 0 ? void 0 : _b.id,
                                    currencyIsoCode: (_c = variant === null || variant === void 0 ? void 0 : variant.currency) === null || _c === void 0 ? void 0 : _c.isoCode,
                                },
                            }));
                        }, title: t('details') })),
                    React.createElement(InputNumber, { name: variant.id, value: (_d = variant === null || variant === void 0 ? void 0 : variant.price) !== null && _d !== void 0 ? _d : '', onChange: handleInputChange, loading: savingKeys.has(variant.id), disabled: isProduction, decimal: 2, error: showErrors && variant.price === null }),
                    React.createElement(CurrencySelect, { price: (_e = variant === null || variant === void 0 ? void 0 : variant.price) !== null && _e !== void 0 ? _e : undefined, name: variant.id, value: isProduction
                            ? ((_f = variant === null || variant === void 0 ? void 0 : variant.currency) === null || _f === void 0 ? void 0 : _f.isoCode) || ''
                            : ((_g = variant === null || variant === void 0 ? void 0 : variant.currency) === null || _g === void 0 ? void 0 : _g.isoCode) || defaultCurrencyIsoCode, error: showErrors && variant.currency === null, onChange: handleCurrencyChange, disabled: isProduction })))));
    };
    if (!openedOnStartIds.current) {
        openedOnStartIds.current = new Set(components
            .filter((component) => componentHasProblems(component.id))
            .map((component) => component.id));
    }
    return (React.createElement(Grid, { gap: 24, overflow: true },
        React.createElement(Header, { size: "m" }, t('components_cost')),
        categoriezdComponents.map((category) => (React.createElement("div", { key: category.categoryId },
            React.createElement(TableGrid, { columns: [
                    ['700px', '70%'],
                    ['300px', '30%'],
                ] },
                React.createElement(Head, null,
                    React.createElement(Row, { head: true },
                        React.createElement(Cell, null, getCategoryLabel(category, i18n)),
                        React.createElement(Cell, { align: "right" }, t('price_capitalize')))),
                category.items.map((component) => {
                    var _a;
                    const componentCosts = getComponentCosts(component.id) || [];
                    const variantRows = componentCosts.map((variant) => renderVariantRow(component, variant));
                    if (componentCosts.length <= 1) {
                        return (React.createElement(Body, { key: component.id },
                            renderComponentRow(component, componentCosts.length === 0 &&
                                t('product_variant', { count: componentCosts.length })),
                            variantRows));
                    }
                    return (React.createElement(Body, { key: component.id },
                        React.createElement(Accordion, { header: renderComponentRow(component, React.createElement(React.Fragment, null,
                                t('product_variant', { count: componentCosts.length }),
                                React.createElement("span", { "data-accordion-chevron": true },
                                    React.createElement(ChevronDownSvg, null)))), opened: (_a = openedOnStartIds.current) === null || _a === void 0 ? void 0 : _a.has(component.id) }, variantRows)));
                })))))));
}
export default ComponentCosts;
